"use client";

import type { JSX } from "react";
import { Icon, Switch, Text, View } from "@/components";
import { useLayout } from "@/providers";
import type { CourseFlags } from "@/types/elearning";

export interface EvaluationFlagsEditorProps {
  /** Banderas actuales del curso. */
  flags: CourseFlags;
  /** Cambio de banderas (la página decide cuándo persistir). */
  onChange: (flags: CourseFlags) => void;
  /**
   * Muestra la advertencia de integridad al editar un curso existente
   * (cambiar flags con inscripciones/intentos en curso; política por definir
   * en el backend — la UI reserva el aviso).
   */
  showIntegrityWarning?: boolean;
}

/**
 * @name EvaluationFlagsEditor
 * @description Editor de las tres banderas de evaluación del curso (G2/G3) con
 * vista previa de la lógica resultante (matriz de la especificación §3): cómo se
 * completa un módulo, cuándo se aprueba el curso y si se emite certificado.
 * La vista previa traduce la combinación ANTES de guardar, para que el editor
 * entienda el efecto.
 *
 * @param {EvaluationFlagsEditorProps} props - Banderas, callback y aviso de integridad.
 * @returns {JSX.Element} El editor renderizado.
 */
const EvaluationFlagsEditor = ({
  flags,
  onChange,
  showIntegrityWarning = false
}: EvaluationFlagsEditorProps): JSX.Element => {
  const { t } = useLayout();

  const rows: Array<{
    key: keyof CourseFlags;
    title: string;
    description: string;
  }> = [
    {
      key: "hasModuleQuizzes",
      title: t("moduleQuiz"),
      description: t("flagQuizDescription")
    },
    {
      key: "hasFinalExam",
      title: t("finalExam"),
      description: t("flagExamDescription")
    },
    {
      key: "issuesCertificate",
      title: t("issuesCertificate"),
      description: t("flagCertificateDescription")
    }
  ];

  const { hasModuleQuizzes: quiz, hasFinalExam: examen } = flags;
  const preview = [
    `• ${t("previewModuleCompletes")}: ${
      quiz ? t("previewVideoPlusQuiz") : t("previewVideoOnly")
    }`,
    `• ${t("previewCourseApproved")}: ${
      examen
        ? t("previewAllModulesPlusExam")
        : quiz
          ? t("previewAllModulesComplete")
          : t("previewAllModulesWatched")
    }`,
    `• ${t("certificate")}: ${
      flags.issuesCertificate
        ? t("certificateIssued")
        : t("noCertificateIssued")
    }`
  ];

  return (
    <View className="flex flex-col gap-4">
      {rows.map((row) => (
        <View
          key={row.key}
          className="flex items-center justify-between gap-4 p-4 rounded-xl border border-slate-200 dark:border-slate-800"
        >
          <View className="flex flex-col gap-0.5">
            <Text className="text-sm font-medium text-foreground">
              {row.title}
            </Text>
            <Text className="text-xs text-muted-foreground">
              {row.description}
            </Text>
          </View>
          <Switch
            label={`flag-${row.key}`}
            isChecked={flags[row.key]}
            onChange={(checked) => onChange({ ...flags, [row.key]: checked })}
          />
        </View>
      ))}

      <View className="p-4 rounded-xl bg-primary/5 border border-primary/20 flex flex-col gap-1.5">
        <Text className="text-xs font-semibold text-primary">
          {t("resultingLogicPreview")}
        </Text>
        {preview.map((line) => (
          <Text key={line} className="text-xs text-foreground">
            {line}
          </Text>
        ))}
      </View>

      {showIntegrityWarning && (
        <View className="p-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-900 flex items-start gap-3">
          <Icon
            name="fa-solid fa-triangle-exclamation"
            className="text-amber-600 dark:text-amber-400 text-xs!"
          />
          <Text className="text-xs text-amber-800 dark:text-amber-300">
            {t("flagsIntegrityWarning")}
          </Text>
        </View>
      )}
    </View>
  );
};

export default EvaluationFlagsEditor;
